import React from "react";
import { Box, Button, TextField, Typography, Modal, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { CardElement, Elements, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { clearStoredPromoCode, codesMatch } from "../../utils/promoCapture";

const SOLVER_API_URL = process.env.REACT_APP_SOLVER_API_URL || "http://localhost:3001";
const STRIPE_PUBLISHABLE_KEY =
  process.env.REACT_APP_STRIPE_LOAD_KEY || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";
const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

const COLORS = {
  bg: "#0e0e0e",
  card: "#1a1a1a",
  border: "#333",
  text: "#e8e8e8",
  muted: "#9a9a9a",
  green: "#56ECCF",
  gold: "#B99E4F",
  white: "#ffffff",
};

const PLANS = [
  {
    id: "starter",
    name: "Starter",
    tagline: "Test the waters",
    price: 11.95,
    features: [
      "Personalised Poker Learning",
      "Dashboard Feedback",
      "Preflop + Flop Trainer",
      "Low stakes Play Opponents",
      "Preflop Solver",
    ],
    missing: [],
  },
  {
    id: "improver",
    name: "Improver",
    tagline: "Crush the competition",
    price: 14.95,
    featured: true,
    features: [
      "Personalised Poker Learning",
      "Dashboard Feedback",
      "Unlimited Trainer",
      "All Play Opponents",
      "Unlimited Solver",
      "Solver Expected Values",
      "Solver Combo/Range Analysis",
    ],
    missing: [],
  },
  {
    id: "pro",
    name: "Pro",
    tagline: "Theory and personal hand feedback",
    price: 24.95,
    features: [
      "Personalised Poker Learning",
      "Dashboard Feedback",
      "Unlimited Trainer",
      "All Play Opponents",
      "Unlimited Solver",
      "Solver Expected Values",
      "Solver Combo/Range Analysis",
      "Online Hand Analysis and Review",
    ],
    missing: [],
  },
];

export { PLANS };

function formatPlanPrice(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return String(n);
  return Number.isInteger(v) ? String(v) : v.toFixed(2);
}

export { formatPlanPrice };

const CARD_ELEMENT_OPTIONS = {
  hidePostalCode: true,
  style: {
    base: {
      color: "#ffffff",
      fontSize: "14px",
      "::placeholder": { color: "#8e8e8e" },
      iconColor: "#bdbdbd",
    },
    invalid: { color: "#ff7d7d", iconColor: "#ff7d7d" },
  },
};

function CheckoutForm({ planId, busy, onSubmit, onError, discountInput, onDiscountChange, promoActive, expectedPromo }) {
  const stripe = useStripe();
  const elements = useElements();
  const [cardholderName, setCardholderName] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const planPrice = PLANS.find((p) => p.id === planId)?.price;
  const checkoutPrice = promoActive ? Math.round(Number(planPrice) * 0.7 * 100) / 100 : planPrice;

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    try {
      const card = elements.getElement(CardElement);
      if (!card) {
        onError?.("Card form is not ready yet.");
        return;
      }
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card,
        billing_details: { name: String(cardholderName || "").trim() || undefined },
      });
      if (error || !paymentMethod?.id) {
        onError?.(error?.message || "Could not read card details.");
        return;
      }
      await onSubmit?.({
        plan: planId,
        paymentMethodId: paymentMethod.id,
        cardholderName: String(cardholderName || "").trim(),
        referralCode: String(discountInput || "").trim() || undefined,
        applyTrialCoupon: !!promoActive,
        stripe, // for 3DS confirmation in parent
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1.5 }}>
      <TextField
        size="small"
        placeholder="Name on card"
        value={cardholderName}
        onChange={(e) => setCardholderName(e.target.value)}
        sx={{
          "& .MuiInputBase-input": { color: COLORS.white, fontSize: 13 },
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
        }}
      />
      <Box sx={{ border: "1px solid #3a3a3a", borderRadius: 1, px: 1, py: 1.1, backgroundColor: "#141414" }}>
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </Box>
      <TextField
        size="small"
        placeholder="Discount code (optional)"
        value={discountInput}
        onChange={(e) => onDiscountChange?.(e.target.value)}
        sx={{
          "& .MuiInputBase-input": { color: COLORS.white, fontSize: 13 },
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
        }}
      />
      {promoActive && (
        <Typography sx={{ color: COLORS.gold, fontSize: 12, fontWeight: 700 }}>
          {expectedPromo} applied: 30% off first month (${formatPlanPrice(checkoutPrice)} due today)
        </Typography>
      )}
      <Button
        variant="contained"
        disabled={busy || submitting || !stripe}
        onClick={handlePay}
        sx={{
          backgroundColor: COLORS.green,
          color: "#111",
          fontWeight: 800,
          textTransform: "none",
          "&:hover": { backgroundColor: "rgb(45, 179, 155)" },
        }}
      >
        {submitting || busy
          ? "Processing..."
          : `Subscribe, $${formatPlanPrice(checkoutPrice)}/mo`}
      </Button>
    </Box>
  );
}

/**
 * Plan picker / checkout. Dismissible so users can still browse the app.
 * mode: "subscribe" (new) | "change" (existing paid sub switching plan)
 */
export default function PlanPaywall({
  open,
  onClose,
  onSubscribed,
  name = "",
  trialEndPromoCode = "ZV3PGBRG",
  trialEndPromoCodes = null,
  trialActive = false,
  initialPlan = "improver",
  currentPlan = null,
  mode = "subscribe",
  headlineOverride = null,
  subtitleOverride = null,
  hideHostedCheckout = false,
}) {
  const [selected, setSelected] = React.useState(initialPlan || "improver");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [info, setInfo] = React.useState("");
  const acceptedPromos = React.useMemo(() => {
    const list = Array.isArray(trialEndPromoCodes) ? trialEndPromoCodes : [];
    const primary = String(trialEndPromoCode || "ZV3PGBRG").trim();
    return Array.from(
      new Set([primary, ...list].map((c) => String(c || "").trim().toUpperCase()).filter(Boolean))
    );
  }, [trialEndPromoCode, trialEndPromoCodes]);
  const expectedPromo = acceptedPromos[0] || "ZV3PGBRG";
  const [discountInput, setDiscountInput] = React.useState("");
  const isChangeMode = mode === "change";

  React.useEffect(() => {
    if (!open) return;
    const next = String(initialPlan || "improver").toLowerCase();
    setSelected(PLANS.some((p) => p.id === next) ? next : "improver");
    setDiscountInput("");
    setError("");
    setInfo("");
  }, [open, initialPlan]);

  if (!open) return null;

  const displayName = String(name || "").trim().split(/\s+/)[0] || "";
  const subtitle =
    subtitleOverride ||
    (displayName
      ? `Choose your plan ${displayName} to continue your poker career`
      : "Choose your plan to continue your poker career");
  const headline =
    headlineOverride ||
    (isChangeMode ? "Change your plan" : trialActive ? "Upgrade your plan" : "Your free trial has ended");
  const footerNote = isChangeMode
    ? "Switching plans updates your subscription immediately. Proration may apply on your next invoice."
    : trialActive
      ? "Subscribe anytime. Your trial access continues until you pick a plan or the trial ends."
      : "You can close this and browse the app, but features stay locked until you subscribe.";
  const promoActive = !isChangeMode && acceptedPromos.some((code) => codesMatch(discountInput, code));

  const call = async (path, body) => {
    const res = await fetch(`${SOLVER_API_URL}${path}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, data };
  };

  const onCardSubscribe = async (payload) => {
    setError("");
    setInfo("");
    setBusy(true);
    try {
      const stripeClient = payload?.stripe || null;
      const { stripe: _omit, ...body } = payload || {};

      if (isChangeMode) {
        const changed = await call("/api/billing/subscription/change-plan", {
          plan: body.plan || selected,
          paymentMethodId: body.paymentMethodId,
          cardholderName: body.cardholderName,
        });
        if (changed.ok && changed.data?.success) {
          setInfo(
            changed.data?.unchanged
              ? "You are already on this plan."
              : `Switched to ${String(changed.data.plan || selected)}.`
          );
          clearStoredPromoCode();
          window.dispatchEvent(new CustomEvent("pm:billing-refresh"));
          onSubscribed?.(changed.data);
          return;
        }
        if (
          changed.data?.code !== "NO_ACTIVE_SUBSCRIPTION" &&
          changed.data?.code !== "SUBSCRIPTION_NOT_ACTIVE"
        ) {
          setError(changed.data?.error || changed.data?.details || "Could not change plan.");
          return;
        }
      }

      const { ok, data } = await call("/api/billing/subscription/start", {
        ...body,
        plan: body.plan || selected,
        applyTrialCoupon: promoActive || !!body?.applyTrialCoupon,
      });

      if (data?.requiresAction && data?.clientSecret) {
        if (!stripeClient) {
          setError("Card authentication required, but Stripe.js is not ready. Please try again.");
          return;
        }
        const confirmOpts = body?.paymentMethodId
          ? { payment_method: body.paymentMethodId }
          : undefined;
        const confirm = await stripeClient.confirmCardPayment(data.clientSecret, confirmOpts);
        if (confirm?.error) {
          setError(confirm.error.message || "Card authentication failed.");
          return;
        }
        const confirmed = await call("/api/billing/subscription/confirm", {
          subscriptionId: data.subscriptionId,
        });
        if (!confirmed.ok || !confirmed.data?.success) {
          setError(confirmed.data?.error || confirmed.data?.details || "Payment could not be confirmed.");
          return;
        }
        setInfo(promoActive ? "Subscription active with 30% off first month." : "Subscription active.");
        clearStoredPromoCode();
        window.dispatchEvent(new CustomEvent("pm:billing-refresh"));
        onSubscribed?.(confirmed.data);
        return;
      }

      if (!ok || !data?.success) {
        setError(data?.error || data?.details || "Subscription failed.");
        return;
      }
      setInfo(promoActive ? "Subscription active with 30% off first month." : "Subscription active.");
      clearStoredPromoCode();
      window.dispatchEvent(new CustomEvent("pm:billing-refresh"));
      onSubscribed?.(data);
    } catch (_) {
      setError("Could not contact server.");
    } finally {
      setBusy(false);
    }
  };

  const onHostedCheckout = async () => {
    if (hideHostedCheckout || isChangeMode) return;
    setError("");
    setBusy(true);
    try {
      const { ok, data } = await call("/api/billing/checkout-session", {
        plan: selected,
        referralCode: String(discountInput || "").trim() || undefined,
        applyTrialCoupon: promoActive,
        uiMode: "redirect",
      });
      if (!ok || !data?.success || !data?.checkoutUrl) {
        setError(data?.error || "Could not start checkout.");
        return;
      }
      clearStoredPromoCode();
      window.location.href = data.checkoutUrl;
    } catch (_) {
      setError("Could not contact server.");
    } finally {
      setBusy(false);
    }
  };

  const currentPlanId = String(currentPlan || "").toLowerCase();

  return (
    <Modal
      open={open}
      onClose={() => onClose?.()}
      disableEscapeKeyDown={false}
      hideBackdrop={false}
      sx={{ zIndex: 14000 }}
    >
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.94)",
          overflowY: "auto",
          outline: "none",
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 980, mx: "auto", px: 2, py: { xs: 3, md: 5 }, position: "relative" }}>
          <IconButton
            onClick={() => onClose?.()}
            aria-label="Close plans"
            sx={{
              position: "absolute",
              top: { xs: 8, md: 16 },
              right: { xs: 8, md: 8 },
              color: COLORS.muted,
              "&:hover": { color: COLORS.white, backgroundColor: "rgba(255,255,255,0.06)" },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography sx={{ color: COLORS.white, fontSize: { xs: 22, md: 28 }, fontWeight: 800, textAlign: "center", fontFamily: "Play, Arial, sans-serif" }}>
            {headline}
          </Typography>
          <Typography sx={{ color: COLORS.muted, fontSize: 14, textAlign: "center", mt: 1, mb: 3 }}>
            {subtitle}
          </Typography>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 1.5 }}>
            {PLANS.map((plan) => {
              const active = selected === plan.id;
              const isCurrent = currentPlanId === plan.id;
              return (
                <Box
                  key={plan.id}
                  onClick={() => setSelected(plan.id)}
                  sx={{
                    cursor: "pointer",
                    backgroundColor: COLORS.card,
                    border: `2px solid ${active ? COLORS.green : plan.featured ? COLORS.gold : COLORS.border}`,
                    borderRadius: 2,
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.8,
                    boxShadow: active ? `0 0 0 1px ${COLORS.green}` : "none",
                    opacity: isCurrent && !active ? 0.85 : 1,
                  }}
                >
                  {isCurrent && (
                    <Typography sx={{ color: COLORS.green, fontSize: 11, fontWeight: 800, letterSpacing: "0.04em" }}>
                      CURRENT PLAN
                    </Typography>
                  )}
                  {!isCurrent && plan.featured && (
                    <Typography sx={{ color: COLORS.gold, fontSize: 11, fontWeight: 800, letterSpacing: "0.04em" }}>MOST POPULAR</Typography>
                  )}
                  <Typography sx={{ color: COLORS.white, fontSize: 18, fontWeight: 800 }}>{plan.name}</Typography>
                  {plan.tagline && (
                    <Typography sx={{ color: COLORS.muted, fontSize: 13, fontWeight: 600, mt: -0.3 }}>
                      {plan.tagline}
                    </Typography>
                  )}
                  <Typography sx={{ color: COLORS.green, fontSize: 28, fontWeight: 800, lineHeight: 1.1 }}>
                    ${formatPlanPrice(plan.price)}
                    <Box component="span" sx={{ color: COLORS.muted, fontSize: 13, fontWeight: 600 }}> / mo</Box>
                  </Typography>
                  {plan.features.map((f) => (
                    <Typography key={f} sx={{ color: COLORS.text, fontSize: 13 }}>✓ {f}</Typography>
                  ))}
                  {plan.missing.map((f) => (
                    <Typography key={f} sx={{ color: "#666", fontSize: 12 }}>- {f}</Typography>
                  ))}
                </Box>
              );
            })}
          </Box>

          <Box sx={{ mt: 3, maxWidth: 420, mx: "auto", backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 2, p: 2 }}>
            <Typography sx={{ color: COLORS.white, fontWeight: 700, fontSize: 14, mb: 0.5 }}>
              {isChangeMode ? "Confirm change: " : "Checkout: "}
              {PLANS.find((p) => p.id === selected)?.name}
              {promoActive ? " · 30% off first month" : ""}
            </Typography>
            {currentPlanId === selected ? (
              <Typography sx={{ color: COLORS.muted, fontSize: 13, mt: 1 }}>
                This is your current plan.
              </Typography>
            ) : stripePromise ? (
              <Elements stripe={stripePromise}>
                <CheckoutForm
                  planId={selected}
                  busy={busy}
                  onSubmit={onCardSubscribe}
                  onError={(msg) => setError(String(msg || ""))}
                  discountInput={discountInput}
                  onDiscountChange={setDiscountInput}
                  promoActive={promoActive}
                  expectedPromo={expectedPromo}
                />
              </Elements>
            ) : (
              !hideHostedCheckout && (
                <Button
                  variant="contained"
                  onClick={onHostedCheckout}
                  disabled={busy}
                  sx={{ mt: 1, backgroundColor: COLORS.green, color: "#111", fontWeight: 800, textTransform: "none" }}
                >
                  {busy ? "Loading..." : "Continue to Stripe Checkout"}
                </Button>
              )
            )}
            {stripePromise && !hideHostedCheckout && !isChangeMode && (
              <Button onClick={onHostedCheckout} disabled={busy} sx={{ mt: 1, color: COLORS.muted, textTransform: "none", fontSize: 12 }}>
                Prefer hosted Stripe Checkout
              </Button>
            )}
            {error && <Typography sx={{ color: "#ff7d7d", fontSize: 12, mt: 1 }}>{error}</Typography>}
            {info && <Typography sx={{ color: COLORS.green, fontSize: 12, mt: 1 }}>{info}</Typography>}
          </Box>

          <Typography sx={{ color: COLORS.muted, fontSize: 12, textAlign: "center", mt: 2 }}>
            {footerNote}
          </Typography>
        </Box>
      </Box>
    </Modal>
  );
}
